import { DOMAIN_CALL_STARTED } from '../../actions/actionTypes/domain';
import { USER_PROFILE_FETCH_SUCCESS } from '../../actions/actionTypes/userProfile';
import {
    GET_NEW_SUGGESTIONS,
    HIDE_SUGGESTIONS,
    SHOW_SUGGESTIONS,
    TOGGLE_SUGGESTIONS,
    NEW_SUGGESTIONS,
    SAVE_COUNTER_SUGGESTIONS
} from '../../actions/actionTypes/suggestions';
import { captureException } from '../../../tracker/raven';
import type { SuggestionMessage } from '../../types';

import callSuggestionsMessagesDE from '../config/callSuggestions-de.json';
import callSuggestionsMessagesEN from '../config/callSuggestions-en.json';

const callSuggestionsMessages = {
    'de-DE': callSuggestionsMessagesDE,
    'en-EN': callSuggestionsMessagesEN
};
type SupportedLanguage = keyof typeof callSuggestionsMessages;
type SuggestionsDictionary = Record<string, string[]>;

export const TYPE_SUPER_WORD = 'SuperWord';
export const TYPE_SPEECH_RATE_KEY = 'SpeechRate';
export const TYPE_DO_SAY_RATE = 'DoSayRate';
export const TYPE_DO_NOT_SAY_RATE = 'DoNotSayRate';

const NEGATIVE_KEY = 'negative';
const POSITIVE_KEY = 'positive';

function randomInteger(min: number, max: number): number {
    return Math.floor(Math.random() * (max - min + 1)) + min;
}

interface CallSuggestionsState {
    callsAfterLastRequest: number;
    callSuggestionsOpen: boolean;
    isCallSuggestionsNew: boolean;
    suggestionsTimestamp: string;
    languageCode: string;
    suggestions: SuggestionMessage[];
}

export const initialState = {
    callsAfterLastRequest: 0,
    callSuggestionsOpen: false,
    isCallSuggestionsNew: false,
    suggestionsTimestamp: '',
    languageCode: 'de-DE',
    suggestions: []
} satisfies CallSuggestionsState;

interface ReducerAction {
    type: string;
    payload?: ReducerPayload;
}

interface ReducerPayload {
    suggestions?: SuggestionMessage[] | IncomingSuggestion[] | unknown;
    timestamp?: string;
    callsAfterLastRequest?: number;
    languageCode?: string;
}

interface IncomingSuggestion {
    type: string;
    positive: boolean;
    keywords?: string[];
}

interface IncomingSuggestionRaw {
    type?: unknown;
    positive?: unknown;
    keywords?: unknown;
}

function toIncomingSuggestions(value: unknown): IncomingSuggestion[] {
    if (!Array.isArray(value)) {
        return [];
    }
    return value.map(item => {
        const source: IncomingSuggestionRaw = typeof item === 'object' && item !== null ? (item as IncomingSuggestionRaw) : {};
        const keywords = Array.isArray(source.keywords) ? source.keywords : [];
        return {
            type: String(source.type || ''),
            positive: Boolean(source.positive),
            keywords: keywords.map(keyword => String(keyword))
        };
    });
}

function isSuggestionMessage(value: unknown): value is SuggestionMessage {
    if (!value || typeof value !== 'object') {
        return false;
    }
    const candidate = value as {
        type?: unknown;
        keyword?: unknown;
        message?: unknown;
        positive?: unknown;
        templateIndex?: unknown;
    };
    return (
        typeof candidate.type === 'string' &&
        typeof candidate.keyword === 'string' &&
        typeof candidate.message === 'string' &&
        typeof candidate.positive === 'boolean' &&
        typeof candidate.templateIndex === 'number'
    );
}

function getShowSuggestionsPayload(payload?: ReducerPayload) {
    const suggestions =
        payload && Array.isArray(payload.suggestions)
            ? payload.suggestions.filter((item): item is SuggestionMessage => isSuggestionMessage(item))
            : undefined;
    const timestamp = typeof payload?.timestamp === 'string' ? payload.timestamp : undefined;
    return { suggestions, timestamp };
}

function getMessagesByLanguage(lang: SupportedLanguage): SuggestionsDictionary {
    const source = callSuggestionsMessages[lang];
    if (!source || typeof source !== 'object') {
        return {};
    }
    return Object.entries(source).reduce((accumulator: SuggestionsDictionary, [key, value]) => {
        if (Array.isArray(value) && value.every(item => typeof item === 'string')) {
            accumulator[key] = value;
        }
        return accumulator;
    }, {});
}

const callSuggestions = (state: CallSuggestionsState | undefined, action: ReducerAction): CallSuggestionsState => {
    const currentState = state ?? initialState;
    const { type } = action;
    const payload = action.payload || {};
    switch (type) {
        case GET_NEW_SUGGESTIONS: {
            return currentState;
        }

        case SHOW_SUGGESTIONS: {
            const { suggestions, timestamp } = getShowSuggestionsPayload(action.payload);

            return {
                ...currentState,
                callSuggestionsOpen: true,
                suggestionsTimestamp: timestamp || currentState.suggestionsTimestamp,
                suggestions: suggestions || currentState.suggestions
            };
        }

        case HIDE_SUGGESTIONS: {
            return { ...currentState, callSuggestionsOpen: false, isCallSuggestionsNew: false };
        }

        case TOGGLE_SUGGESTIONS: {
            return {
                ...currentState,
                callSuggestionsOpen: !currentState.callSuggestionsOpen,
                isCallSuggestionsNew: !currentState.callSuggestionsOpen ? currentState.isCallSuggestionsNew : false
            };
        }

        case NEW_SUGGESTIONS: {
            if (payload.timestamp !== currentState.suggestionsTimestamp) {
                return {
                    ...currentState,
                    callSuggestionsOpen: !!payload.timestamp,
                    isCallSuggestionsNew: !!payload.timestamp,
                    suggestionsTimestamp: String(payload.timestamp || ''),
                    suggestions: toIncomingSuggestions(payload.suggestions).reduce(
                        (accumulator: SuggestionMessage[], currentValue: IncomingSuggestion) => {
                            try {
                                const push = (messageKeyPath: string, keyword = '') => {
                                    const lang: SupportedLanguage = currentState.languageCode === 'en-EN' ? 'en-EN' : 'de-DE';
                                    const byLanguage = getMessagesByLanguage(lang);
                                    const messages = byLanguage[messageKeyPath] || [];
                                    const templateIndex = randomInteger(1, messages.length - 1);

                                    accumulator.push({
                                        type: currentValue.type,
                                        keyword,
                                        positive: currentValue.positive,
                                        message: messages[templateIndex],
                                        templateIndex
                                    });
                                };

                                const keywords = (currentValue.keywords || []).filter((k: string) => !!k);
                                const messageKey = `${currentValue.type}_${currentValue.positive ? POSITIVE_KEY : NEGATIVE_KEY}`;

                                if (
                                    (currentValue.type === TYPE_SUPER_WORD && keywords.length === 0) ||
                                    (currentValue.type === TYPE_DO_SAY_RATE && keywords.length === 0 && !currentValue.positive) ||
                                    (currentValue.type === TYPE_DO_NOT_SAY_RATE && keywords.length === 0 && !currentValue.positive) ||
                                    (currentValue.type === TYPE_SPEECH_RATE_KEY && keywords.length === 0 && !currentValue.positive)
                                ) {
                                    return accumulator;
                                }

                                if (currentValue.type === TYPE_SPEECH_RATE_KEY) {
                                    if (!currentValue.positive) {
                                        push(`${messageKey}_${keywords[0]}`);
                                    } else {
                                        push(messageKey);
                                    }
                                } else if (keywords.length > 0) {
                                    keywords.forEach(keyword => push(messageKey, keyword));
                                } else {
                                    push(messageKey);
                                }
                            } catch (e) {
                                captureException(e);
                            }

                            return accumulator;
                        },
                        []
                    )
                };
            }
            return currentState;
        }

        case SAVE_COUNTER_SUGGESTIONS: {
            return {
                ...currentState,
                callsAfterLastRequest: Number(payload.callsAfterLastRequest) || 0
            };
        }

        case USER_PROFILE_FETCH_SUCCESS: {
            return {
                ...currentState,
                languageCode: String(payload.languageCode || currentState.languageCode)
            };
        }

        case DOMAIN_CALL_STARTED: {
            return {
                ...currentState,
                callSuggestionsOpen: false
            };
        }

        default: {
            return currentState;
        }
    }
};

export default callSuggestions;
